import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { microsoftVerbundeneQuery } from '../lib/queries'
import { Card, CardPad } from './ui/primitives'

/**
 * **Die Einrichtungskarte für Outlook und To Do je Person – für die Administration**
 * (Z-17, Nils: *„Die Anleitung muss in Work. Wie starte ich? Wie kann ich einbinden?"*).
 *
 * Steht unter der Postfach-Karte in KI & Einstellungen, weil es **dieselbe** App-Registrierung
 * in Entra ist: die drei Angaben (Tenant, Client-ID, Geheimnis) kommen von dort. Was hier fehlt,
 * ist nur die Erlaubnis – die delegierten Berechtigungen und die Rücksprungadresse. Beides steht
 * hier zum Abschreiben bzw. Kopieren, die Rücksprungadresse aus der Adresse dieser Seite, damit
 * sie zur Umgebung passt und nicht aus einer Datei abgetippt werden muss.
 *
 * Darunter: wer verbunden ist. Das ist die Antwort auf „hat es geklappt?", und die gehört auf den
 * Bildschirm, nicht ins Protokoll.
 */
const BERECHTIGUNGEN = [
  ['Mail.ReadWrite', 'gekennzeichnete Mails lesen und die Kennzeichnung abhaken'],
  ['Tasks.ReadWrite', 'Aufgaben aus Microsoft To Do lesen und dort abhaken'],
  ['Calendars.Read', 'die heutigen Termine – nur lesen'],
  ['User.Read', 'Konto und Anzeigename für die Seitenleiste'],
  ['offline_access', 'der dauerhafte Zugang, damit sich niemand stündlich neu anmeldet'],
] as const

export function MicrosoftEinrichtung() {
  const { data: verbundene } = useQuery(microsoftVerbundeneQuery)
  const rueckweg = `${window.location.origin}/api/microsoft/callback`
  const [kopiert, setKopiert] = useState(false)

  return (
    <Card>
      <CardPad>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h3 className="text-sm font-bold">Outlook und To Do je Person</h3>
          <span className="text-[11.5px] text-muted">
            jeder verbindet sein eigenes Konto – nichts läuft über work@
          </span>
        </div>

        <p className="mt-2 text-[12.5px] text-ink2">
          Dieselbe App-Registrierung wie das Postfach oben, nur mit <b>delegierten</b>{' '}
          Berechtigungen: Work handelt dann im Namen der Person, die sich verbunden hat, und darf
          nur, was sie darf. Einmal in Entra einrichten, danach klickt jede Person selbst auf „Mit
          Microsoft 365 verbinden" in der Seitenleiste.
        </p>

        <ol className="mt-3 list-decimal space-y-2 pl-5 text-[12.5px] text-ink2">
          <li>
            <a
              href="https://entra.microsoft.com"
              target="_blank"
              rel="noreferrer"
              className="text-accent-deep hover:underline"
            >
              entra.microsoft.com
            </a>{' '}
            → <b>App-Registrierungen</b> → die Work-App (die mit der Client-ID von oben).
          </li>
          <li>
            <b>Authentifizierung</b> → „Plattform hinzufügen" → <b>Web</b> → als Umleitungs-URI:
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <code className="rounded-md bg-[#f3f2ef] px-2 py-1 font-mono text-[12px] break-all">
                {rueckweg}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(rueckweg).then(() => {
                    setKopiert(true)
                    window.setTimeout(() => setKopiert(false), 2000)
                  })
                }}
                className="cursor-pointer text-[11.5px] font-semibold text-accent-deep hover:underline"
              >
                {kopiert ? 'kopiert ✓' : 'kopieren'}
              </button>
            </div>
            Kein implizites Gewähren, keine Abmelde-URL nötig.
          </li>
          <li>
            <b>API-Berechtigungen</b> → „Berechtigung hinzufügen" → Microsoft Graph →{' '}
            <b>Delegierte Berechtigungen</b> → diese fünf ankreuzen:
            <ul className="mt-1 space-y-0.5">
              {BERECHTIGUNGEN.map(([name, wofuer]) => (
                <li key={name} className="flex flex-wrap gap-x-2">
                  <code className="font-mono text-[12px] text-ink">{name}</code>
                  <span className="text-muted">{wofuer}</span>
                </li>
              ))}
            </ul>
            Die vorhandene <b>Anwendungs</b>berechtigung{' '}
            <code className="font-mono">Mail.Send</code> für das Postfach bleibt, sie hat damit
            nichts zu tun.
          </li>
          <li>
            Falls der Mandant es verlangt: <b>„Administratorzustimmung für … erteilen"</b>. Sonst
            fragt Microsoft jede Person beim ersten Verbinden selbst.
          </li>
          <li>
            Dann in Work: Seitenleiste unten → <b>„Mit Microsoft 365 verbinden"</b>. Die Anleitung
            für die Person steht dort hinter „Wie geht das?".
          </li>
        </ol>

        <div className="mt-4 border-t border-line pt-3">
          <div className="text-[12px] font-semibold text-ink2">
            Verbunden{verbundene ? ` · ${verbundene.length}` : ''}
          </div>
          {!verbundene || verbundene.length === 0 ? (
            <p className="mt-1 text-[12px] text-muted">
              Noch niemand. Sobald sich jemand verbindet, steht er hier – mit letztem Abgleich und
              Fehler, falls einer war.
            </p>
          ) : (
            <ul className="mt-1 space-y-1 text-[12px]">
              {verbundene.map((v) => (
                <li key={v.personId} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-semibold">{v.name}</span>
                  <span className="text-muted">{v.konto}</span>
                  {!v.todo && <span className="text-muted">· ohne To Do (neu verbinden)</span>}
                  {v.letzterAbgleichAm && (
                    <span className="text-muted">
                      · zuletzt {new Date(v.letzterAbgleichAm).toLocaleString('de-DE')}
                    </span>
                  )}
                  {!v.aktiv && <span className="text-warn">· abgelaufen</span>}
                  {v.letzterFehler && (
                    <span className="basis-full text-warn">{v.letzterFehler}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardPad>
    </Card>
  )
}
